package com.trevorwiebe.timesheet.loads.data.dto

import com.trevorwiebe.timesheet.admin.loadrate.data.dto.parseLoadRateCategory
import com.trevorwiebe.timesheet.admin.loadrate.data.dto.parseLoadRateType
import com.trevorwiebe.timesheet.admin.loadrate.data.dto.parseLoadRateUnit
import com.trevorwiebe.timesheet.loads.domain.model.Load
import com.trevorwiebe.timesheet.loads.domain.model.LoadAttachment
import com.trevorwiebe.timesheet.loads.domain.model.LoadRateInfo
import com.trevorwiebe.timesheet.loads.domain.model.LoadStop
import com.trevorwiebe.timesheet.loads.domain.model.StopType
import kotlinx.datetime.LocalDate
import kotlinx.serialization.SerialName
import kotlinx.serialization.Serializable

@Serializable
data class LoadDto(
    val customerId: String,
    val loadNumber: String = "",
    val po: String = "",
    val orderNumber: String = "",
    val commodity: String = "",
    val customerRequirements: String = "",
    val pickupDate: String,
    val originalDate: String,
    val scheduledDate: String? = null,
    val stops: List<LoadStopDto>,
    val employeeId: String,
    @SerialName("brokerId")
    val carrierId: String = "",
    val dispatchConfirmed: Boolean = false,
    val setUp: Boolean = false,
    val ready: Boolean = false,
    val rate: LoadRateInfoDto? = null,
    val attachments: List<LoadAttachmentDto> = emptyList(),
    val truckId: String = "",
)

@Serializable
data class LoadStopDto(
    val locationId: String,
    val type: String,
)

@Serializable
data class LoadAttachmentDto(
    val name: String = "",
    val url: String = "",
    val uploadedById: String = "",
)

fun LoadAttachmentDto.toLoadAttachment(): LoadAttachment =
    LoadAttachment(name = name, url = url, uploadedById = uploadedById)

fun LoadAttachment.toLoadAttachmentDto(): LoadAttachmentDto =
    LoadAttachmentDto(name = name, url = url, uploadedById = uploadedById)

@Serializable
data class LoadRateInfoDto(
    val parentRateId: String = "",
    val rateCategory: String = "",
    val rateType: String = "",
    val unit: String? = null,
    val perUnit: Int = 0,
    val pickupLocationId: String = "",
    val dropLocationId: String = "",
    val flatRateValue: Int = 0,
    val unitQuantity: Double = 0.0,
    val otherCharge: Int = 0,
    val stopOffCharge: Int = 0,
    val detention: Int = 0,
    val fuelSurchargePercent: Double = 0.0,
)

fun LoadDto.toLoad(id: String): Load = Load(
    id = id,
    customerId = customerId,
    loadNumber = loadNumber,
    po = po,
    orderNumber = orderNumber,
    commodity = commodity,
    customerRequirements = customerRequirements,
    pickupDate = LocalDate.parse(pickupDate),
    originalDate = LocalDate.parse(originalDate),
    scheduledDate = scheduledDate?.let { LocalDate.parse(it) },
    stops = stops.map { it.toLoadStop() },
    employeeId = employeeId,
    carrierId = carrierId,
    dispatchConfirmed = dispatchConfirmed,
    setUp = setUp,
    ready = ready,
    rate = rate?.toLoadRateInfo(),
    attachments = attachments.map { it.toLoadAttachment() },
    truckId = truckId,
)

fun LoadRateInfoDto.toLoadRateInfo(): LoadRateInfo = LoadRateInfo(
    parentRateId = parentRateId,
    rateCategory = parseLoadRateCategory(rateCategory),
    rateType = parseLoadRateType(rateType),
    unit = parseLoadRateUnit(unit),
    perUnit = perUnit,
    pickupLocationId = pickupLocationId,
    dropLocationId = dropLocationId,
    flatRateValue = flatRateValue,
    unitQuantity = unitQuantity,
    otherCharge = otherCharge,
    stopOffCharge = stopOffCharge,
    detention = detention,
    fuelSurchargePercent = fuelSurchargePercent,
)

fun LoadStopDto.toLoadStop(): LoadStop {
    return LoadStop(
        locationId = locationId,
        type = StopType.valueOf(type),
    )
}

fun Load.toLoadDto(): LoadDto = LoadDto(
    customerId = customerId,
    loadNumber = loadNumber,
    po = po,
    orderNumber = orderNumber,
    commodity = commodity,
    customerRequirements = customerRequirements,
    pickupDate = pickupDate.toString(),
    originalDate = originalDate.toString(),
    scheduledDate = scheduledDate?.toString(),
    stops = stops.map { it.toLoadStopDto() },
    employeeId = employeeId,
    carrierId = carrierId,
    dispatchConfirmed = dispatchConfirmed,
    setUp = setUp,
    ready = ready,
    rate = rate?.toLoadRateInfoDto(),
    attachments = attachments.map { it.toLoadAttachmentDto() },
    truckId = truckId,
)

fun LoadRateInfo.toLoadRateInfoDto(): LoadRateInfoDto = LoadRateInfoDto(
    parentRateId = parentRateId,
    rateCategory = rateCategory.name,
    rateType = rateType.name,
    unit = unit?.name,
    perUnit = perUnit,
    pickupLocationId = pickupLocationId,
    dropLocationId = dropLocationId,
    flatRateValue = flatRateValue,
    unitQuantity = unitQuantity,
    otherCharge = otherCharge,
    stopOffCharge = stopOffCharge,
    detention = detention,
    fuelSurchargePercent = fuelSurchargePercent,
)

fun LoadStop.toLoadStopDto(): LoadStopDto = LoadStopDto(
    locationId = locationId,
    type = type.name,
)
